import { useCallback, useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import {
  getVendorDirectorySettings,
  updateVendorDirectorySettings,
} from "../../api/clientVendorDirectorySettingsApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.settings";
const RANKING_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.ranking";

export default function ClientVendorDirectorySettingsPage() {
  const { clientViewId } = useParams();
  const { activeView } = useOutletContext() || {};
  const { t } = useLocale();
  const [settings, setSettings] = useState(null);
  const [versionNumber, setVersionNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const viewName = activeView?.name || t(`${RANKING_PREFIX}.thisView`);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getVendorDirectorySettings(clientViewId);
      setSettings(body.settings);
      setVersionNumber(body.versionNumber);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setMessage("");
    try {
      const body = await updateVendorDirectorySettings(clientViewId, {
        settings,
        expectedVersionNumber: versionNumber,
        reason: t(`${COPY_PREFIX}.saveReason`),
      });
      setSettings(body.settings);
      setVersionNumber(body.versionNumber);
      setMessage(t(`${COPY_PREFIX}.saved`));
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
    } finally {
      setBusy(false);
    }
  };

  if (!settings && loading) return <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} />;

  return (
    <div className="page-content cvd-admin">
      <ClientVendorDirectorySubnav clientViewId={clientViewId} />
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.intro`, { viewName })}</p>
        </div>
      </div>
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {message ? <div className="cvd-alert cvd-alert--success">{message}</div> : null}
      {settings ? (
        <div className="cvd-panel">
          <div className="cvd-field">
            <label>
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              />{" "}
              {t(`${COPY_PREFIX}.enabled`)}
            </label>
          </div>
          <div className="cvd-field">
            <label htmlFor="default-page-size">{t(`${COPY_PREFIX}.defaultPageSize`)}</label>
            <input
              id="default-page-size"
              type="number"
              min={5}
              max={settings.maxPageSize || 50}
              value={settings.defaultPageSize}
              onChange={(e) => setSettings({ ...settings, defaultPageSize: Number(e.target.value) })}
            />
          </div>
          <div className="cvd-field">
            <label>
              <input
                type="checkbox"
                checked={settings.rankingDisplay?.showTopVendorBadge}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    rankingDisplay: { ...settings.rankingDisplay, showTopVendorBadge: e.target.checked },
                  })
                }
              />{" "}
              {t(`${COPY_PREFIX}.showTopVendorBadge`)}
            </label>
          </div>
          <div className="cvd-field">
            <label htmlFor="methodology-es">{t(`${COPY_PREFIX}.methodologyEs`)}</label>
            <textarea
              id="methodology-es"
              rows={3}
              value={settings.publicMethodology?.es?.organicPaidStatement || ""}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  publicMethodology: {
                    ...settings.publicMethodology,
                    es: { ...settings.publicMethodology.es, organicPaidStatement: e.target.value },
                  },
                })
              }
            />
          </div>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.SETTINGS_UPDATE}>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>
              {t(`${COPY_PREFIX}.save`)}
            </button>
          </VendorDirectoryPermissionGate>
        </div>
      ) : null}
    </div>
  );
}
